import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../api/client';
import { verifyMagicLink } from '../api/auth';

// MagicLinkConsumer — landar här från en magic-link-mejlklick.
// URL: /auth/m/:token (eller /auth/m?token=...&redirect=...)
//
// Vi anropar backend för att konsumera token, sätter session-cookie via
// Set-Cookie, invaliderar auth-cachen och navigerar vidare. Felfall:
// expired (410), already-used (410), eller nätverk → tydlig felvy med
// "Request a new link"-CTA tillbaka till /login.

type State = 'consuming' | 'success' | 'error';

export default function MagicLinkConsumer() {
  const { token: tokenParam } = useParams<{ token: string }>();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const ranRef = useRef(false);

  const token = tokenParam || search.get('token') || '';
  const redirect = search.get('redirect') || undefined;

  const [state, setState] = useState<State>('consuming');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (ranRef.current) return; // strict mode dubbelmount → kör bara en gång
    ranRef.current = true;

    if (!token || token.length < 16) {
      setState('error');
      setErrorMessage('Invalid sign-in link.');
      return;
    }

    (async () => {
      try {
        const res = await verifyMagicLink(token, redirect);
        // Invalidera auth-cachen så att useAuth refetchar och får ny user
        await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
        setState('success');
        // Liten paus så användaren ser confirmation
        setTimeout(() => {
          navigate(res.redirectTo || '/', { replace: true });
        }, 600);
      } catch (err) {
        setState('error');
        if (err instanceof ApiError) {
          if (err.status === 410) {
            setErrorMessage(
              'This sign-in link has expired or already been used.',
            );
          } else if (err.status === 429) {
            setErrorMessage('Too many attempts. Please try again later.');
          } else {
            setErrorMessage('Could not verify the link. Please try again.');
          }
        } else {
          setErrorMessage('Network error. Please try again.');
        }
      }
    })();
  }, [token, redirect, navigate, queryClient]);

  return (
    <div className="min-h-screen min-h-[100dvh] flex items-center justify-center px-5 relative overflow-hidden">
      <div
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          background: [
            'radial-gradient(ellipse at 30% 20%, rgba(95,168,211,0.10), transparent 50%)',
            'radial-gradient(ellipse at 70% 80%, rgba(230,57,70,0.10), transparent 50%)',
            'radial-gradient(ellipse at center, rgba(0,0,0,0.6), rgba(0,0,0,0.95))',
          ].join(','),
        }}
      />

      <div className="relative z-10 w-[420px] max-w-full text-center">
        {state === 'consuming' ? (
          <>
            <div
              className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-loupe-border-2 mb-5"
              aria-hidden
            >
              <span className="inline-block w-4 h-4 rounded-full border-2 border-loupe-text-3 border-t-loupe-text animate-spin" />
            </div>
            <h1 className="font-display text-2xl mb-2 tracking-[-0.02em]">
              Signing you in…
            </h1>
            <p className="text-sm text-loupe-text-2">One moment.</p>
          </>
        ) : state === 'success' ? (
          <>
            <div
              className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/40 text-emerald-400 mb-5 text-xl"
              aria-hidden
            >
              ✓
            </div>
            <h1 className="font-display text-2xl mb-2 tracking-[-0.02em]">
              Welcome back
            </h1>
            <p className="text-sm text-loupe-text-2">Taking you to Vuelette…</p>
          </>
        ) : (
          <>
            <div
              className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-michelle/10 border border-michelle/40 text-michelle mb-5 text-xl"
              aria-hidden
            >
              !
            </div>
            <h1 className="font-display text-2xl mb-2 tracking-[-0.02em]">
              Could not sign in
            </h1>
            <p className="text-sm text-loupe-text-2 mb-7 leading-relaxed">
              {errorMessage}
            </p>
            <button
              type="button"
              onClick={() => navigate('/login', { replace: true })}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-black text-xs font-semibold tracking-wider uppercase min-h-[44px] hover:scale-[1.02] transition-transform"
            >
              Request a new link →
            </button>
          </>
        )}
      </div>
    </div>
  );
}
